import { useState, useEffect } from 'react';
import api from '../services/api';

interface Course {
  id: string;
  title: string;
  description: string;
  level: 'beginner' | 'intermediate' | 'advanced';
  status: 'draft' | 'published' | 'archived';
  organization_id: string | null;
  created_at: string;
  updated_at: string;
}

interface Module {
  id: string;
  course_id: string;
  title: string;
  description: string | null;
  order_index: number;
  created_at: string;
}

interface Lesson {
  id: string;
  module_id: string;
  title: string;
  content_type: 'video' | 'document' | 'quiz' | 'text';
  content: string | null;
  video_url: string | null;
  duration_minutes: number | null;
  order_index: number;
  quiz_id: string | null;
  created_at: string;
}

const CourseManagement: React.FC = () => {
  const [courses, setCourses] = useState<Course[]>([]);
  const [selectedCourse, setSelectedCourse] = useState<Course | null>(null);
  const [modules, setModules] = useState<Module[]>([]);
  const [lessons, setLessons] = useState<{ [moduleId: string]: Lesson[] }>({});
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  
  // Modal states
  const [showCourseModal, setShowCourseModal] = useState(false);
  const [showModuleModal, setShowModuleModal] = useState(false);
  const [showLessonModal, setShowLessonModal] = useState(false);
  const [editingCourse, setEditingCourse] = useState<Course | null>(null);
  const [editingModule, setEditingModule] = useState<Module | null>(null);
  const [selectedModuleForLesson, setSelectedModuleForLesson] = useState<string | null>(null);

  // Form states
  const [courseForm, setCourseForm] = useState({
    title: '',
    description: '',
    level: 'beginner' as 'beginner' | 'intermediate' | 'advanced',
    status: 'draft' as 'draft' | 'published' | 'archived',
    organization_id: null as string | null
  });

  const [moduleForm, setModuleForm] = useState({
    title: '',
    description: '',
    order_index: 0
  });

  const [lessonForm, setLessonForm] = useState({
    title: '',
    content_type: 'video' as 'video' | 'document' | 'quiz' | 'text',
    content: '',
    video_url: '',
    duration_minutes: 0,
    order_index: 0
  });

  useEffect(() => {
    fetchCourses();
  }, []);

  useEffect(() => {
    if (selectedCourse) {
      fetchModules(selectedCourse.id);
    }
  }, [selectedCourse]);

  const fetchCourses = async () => {
    try {
      setLoading(true);
      const response = await api.get('/courses');
      console.log('Courses API response:', response.data);
      
      // Handle different response formats
      const coursesData = Array.isArray(response.data) 
        ? response.data 
        : (response.data?.data || response.data?.courses || []);
      
      setCourses(coursesData);
    } catch (error: any) {
      console.error('Error fetching courses:', error);
      const errorMsg = error.response?.data?.error || 'Failed to load courses';
      setMessage({ type: 'error', text: errorMsg });
      setCourses([]); // Set empty array on error
    } finally {
      setLoading(false);
    }
  };

  const fetchModules = async (courseId: string) => {
    try {
      const response = await api.get(`/courses/${courseId}/modules`);
      const modulesData = Array.isArray(response.data) 
        ? response.data 
        : (response.data?.data || response.data?.modules || []);
      
      setModules(modulesData);
      
      // Fetch lessons for each module
      for (const module of modulesData) {
        fetchLessons(module.id);
      }
    } catch (error: any) {
      console.error('Error fetching modules:', error);
      const errorMsg = error.response?.data?.error || 'Failed to load modules';
      setMessage({ type: 'error', text: errorMsg });
      setModules([]); // Set empty array on error
    }
  };

  const fetchLessons = async (moduleId: string) => {
    try {
      const response = await api.get(`/courses/modules/${moduleId}/lessons`);
      const lessonsData = Array.isArray(response.data) 
        ? response.data 
        : (response.data?.data || response.data?.lessons || []);
      
      setLessons(prev => ({ ...prev, [moduleId]: lessonsData }));
    } catch (error) {
      console.error('Error fetching lessons:', error);
      setLessons(prev => ({ ...prev, [moduleId]: [] }));
    }
  };

  const handleCreateCourse = async () => {
    if (!courseForm.title || !courseForm.description) {
      setMessage({ type: 'error', text: 'Title and description are required' });
      return;
    }

    try {
      await api.post('/courses', courseForm);
      setMessage({ type: 'success', text: 'Course created successfully' });
      setShowCourseModal(false);
      resetCourseForm();
      fetchCourses();
    } catch (error: any) {
      setMessage({ type: 'error', text: error.response?.data?.error || 'Failed to create course' });
    }
  };

  const handleUpdateCourse = async () => {
    if (!editingCourse || !courseForm.title || !courseForm.description) {
      setMessage({ type: 'error', text: 'Title and description are required' });
      return;
    }

    try {
      await api.put(`/courses/${editingCourse.id}`, courseForm);
      setMessage({ type: 'success', text: 'Course updated successfully' });
      setShowCourseModal(false);
      setEditingCourse(null);
      resetCourseForm();
      fetchCourses();
      if (selectedCourse?.id === editingCourse.id) {
        setSelectedCourse({ ...editingCourse, ...courseForm });
      }
    } catch (error: any) {
      setMessage({ type: 'error', text: error.response?.data?.error || 'Failed to update course' });
    }
  };

  const handleDeleteCourse = async (courseId: string) => {
    if (!confirm('Are you sure you want to delete this course? This will also delete all modules and lessons.')) {
      return;
    }

    try {
      await api.delete(`/courses/${courseId}`);
      setMessage({ type: 'success', text: 'Course deleted successfully' });
      fetchCourses();
      if (selectedCourse?.id === courseId) {
        setSelectedCourse(null);
        setModules([]);
        setLessons({});
      }
    } catch (error: any) {
      setMessage({ type: 'error', text: error.response?.data?.error || 'Failed to delete course' });
    }
  };

  const handleCreateModule = async () => {
    if (!selectedCourse || !moduleForm.title) {
      setMessage({ type: 'error', text: 'Module title is required' });
      return;
    }

    try {
      await api.post(`/courses/${selectedCourse.id}/modules`, moduleForm);
      setMessage({ type: 'success', text: 'Module created successfully' });
      setShowModuleModal(false);
      resetModuleForm();
      fetchModules(selectedCourse.id);
    } catch (error: any) {
      setMessage({ type: 'error', text: error.response?.data?.error || 'Failed to create module' });
    }
  };

  const handleUpdateModule = async () => {
    if (!editingModule || !moduleForm.title) {
      setMessage({ type: 'error', text: 'Module title is required' });
      return;
    }

    try {
      await api.put(`/courses/modules/${editingModule.id}`, moduleForm);
      setMessage({ type: 'success', text: 'Module updated successfully' });
      setShowModuleModal(false);
      setEditingModule(null);
      resetModuleForm();
      if (selectedCourse) {
        fetchModules(selectedCourse.id);
      }
    } catch (error: any) {
      setMessage({ type: 'error', text: error.response?.data?.error || 'Failed to update module' });
    }
  };

  const handleDeleteModule = async (moduleId: string) => {
    if (!confirm('Are you sure you want to delete this module? This will also delete all lessons.')) {
      return;
    }

    try {
      await api.delete(`/courses/modules/${moduleId}`);
      setMessage({ type: 'success', text: 'Module deleted successfully' });
      if (selectedCourse) {
        fetchModules(selectedCourse.id);
      }
    } catch (error: any) {
      setMessage({ type: 'error', text: error.response?.data?.error || 'Failed to delete module' });
    }
  };

  const handleCreateLesson = async () => {
    if (!selectedModuleForLesson || !lessonForm.title) {
      setMessage({ type: 'error', text: 'Lesson title is required' });
      return;
    }

    try {
      await api.post(`/courses/modules/${selectedModuleForLesson}/lessons`, lessonForm);
      setMessage({ type: 'success', text: 'Lesson created successfully' });
      setShowLessonModal(false);
      resetLessonForm();
      fetchLessons(selectedModuleForLesson);
    } catch (error: any) {
      setMessage({ type: 'error', text: error.response?.data?.error || 'Failed to create lesson' });
    }
  };

  const handleDeleteLesson = async (lessonId: string, moduleId: string) => {
    if (!confirm('Are you sure you want to delete this lesson?')) {
      return;
    }

    try {
      await api.delete(`/courses/lessons/${lessonId}`);
      setMessage({ type: 'success', text: 'Lesson deleted successfully' });
      fetchLessons(moduleId);
    } catch (error: any) {
      setMessage({ type: 'error', text: error.response?.data?.error || 'Failed to delete lesson' });
    }
  };

  const resetCourseForm = () => {
    setCourseForm({
      title: '',
      description: '',
      level: 'beginner',
      status: 'draft',
      organization_id: null
    });
  };

  const resetModuleForm = () => {
    setModuleForm({
      title: '',
      description: '',
      order_index: modules.length
    });
  };

  const resetLessonForm = () => {
    setLessonForm({
      title: '',
      content_type: 'video',
      content: '',
      video_url: '',
      duration_minutes: 0,
      order_index: 0
    });
  };

  const openCreateCourseModal = () => {
    resetCourseForm();
    setEditingCourse(null);
    setShowCourseModal(true);
  };

  const openEditCourseModal = (course: Course) => {
    setCourseForm({
      title: course.title,
      description: course.description,
      level: course.level,
      status: course.status,
      organization_id: course.organization_id
    });
    setEditingCourse(course);
    setShowCourseModal(true);
  };

  const openCreateModuleModal = () => {
    resetModuleForm();
    setEditingModule(null);
    setShowModuleModal(true);
  };

  const openEditModuleModal = (module: Module) => {
    setModuleForm({
      title: module.title,
      description: module.description || '',
      order_index: module.order_index
    });
    setEditingModule(module);
    setShowModuleModal(true);
  };

  const openCreateLessonModal = (moduleId: string) => {
    const moduleLessons = lessons[moduleId] || [];
    setLessonForm({
      ...lessonForm,
      order_index: moduleLessons.length
    });
    setSelectedModuleForLesson(moduleId);
    setShowLessonModal(true);
  };

  const getLevelBadge = (level: string) => {
    const colors = {
      beginner: 'bg-green-500/20 text-green-300',
      intermediate: 'bg-yellow-500/20 text-yellow-300',
      advanced: 'bg-red-500/20 text-red-300'
    };
    return colors[level as keyof typeof colors] || colors.beginner;
  };

  const getStatusBadge = (status: string) => {
    const colors = {
      draft: 'bg-slate-500/20 text-slate-300',
      published: 'bg-blue-500/20 text-blue-300',
      archived: 'bg-gray-500/20 text-gray-300'
    };
    return colors[status as keyof typeof colors] || colors.draft;
  };

  const getContentTypeIcon = (type: string) => {
    const icons = {
      video: '🎥',
      document: '📄',
      quiz: '📝',
      text: '📖'
    };
    return icons[type as keyof typeof icons] || '📄';
  };

  if (loading) {
    return <div className="text-center py-8">Loading courses...</div>;
  }

  return (
    <div className="space-y-6">
      {message && (
        <div className={`p-4 rounded-lg ${
          message.type === 'success' 
            ? 'bg-green-500/20 border border-green-500/30 text-green-300' 
            : 'bg-red-500/20 border border-red-500/30 text-red-300'
        }`}>
          {message.text}
        </div>
      )}

      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold">Course Management</h2>
        <button
          onClick={openCreateCourseModal}
          className="btn btn-primary"
        >
          + Create Course
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Courses List */}
        <div className="lg:col-span-1">
          <div className="card">
            <h3 className="text-lg font-semibold mb-4">Courses ({courses.length})</h3>
            <div className="space-y-2">
              {courses.map(course => (
                <div
                  key={course.id}
                  className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                    selectedCourse?.id === course.id
                      ? 'bg-primary-500/20 border-primary-500'
                      : 'bg-slate-800 border-slate-700 hover:border-slate-600'
                  }`}
                  onClick={() => setSelectedCourse(course)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h4 className="font-semibold text-sm">{course.title}</h4>
                      <div className="flex items-center gap-2 mt-2">
                        <span className={`text-xs px-2 py-0.5 rounded ${getLevelBadge(course.level)}`}>
                          {course.level}
                        </span>
                        <span className={`text-xs px-2 py-0.5 rounded ${getStatusBadge(course.status)}`}>
                          {course.status}
                        </span>
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          openEditCourseModal(course);
                        }}
                        className="text-blue-400 hover:text-blue-300 text-sm"
                      >
                        ✏️
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteCourse(course.id);
                        }}
                        className="text-red-400 hover:text-red-300 text-sm"
                      >
                        🗑️
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Course Details */}
        <div className="lg:col-span-2">
          {selectedCourse ? (
            <div className="card">
              <div className="flex items-start justify-between mb-6">
                <div>
                  <h3 className="text-xl font-bold">{selectedCourse.title}</h3>
                  <p className="text-slate-400 mt-1">{selectedCourse.description}</p>
                </div>
                <button
                  onClick={openCreateModuleModal}
                  className="btn btn-primary"
                >
                  + Add Module
                </button>
              </div>

              {/* Modules */}
              <div className="space-y-4">
                {modules.length === 0 ? (
                  <div className="text-center py-8 text-slate-400">
                    No modules yet. Create your first module to get started.
                  </div>
                ) : (
                  modules.map((module, index) => (
                    <div key={module.id} className="bg-slate-800 rounded-lg p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <h4 className="font-semibold">
                            Module {index + 1}: {module.title}
                          </h4>
                          {module.description && (
                            <p className="text-sm text-slate-400 mt-1">{module.description}</p>
                          )}
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => openCreateLessonModal(module.id)}
                            className="text-sm text-primary-400 hover:text-primary-300"
                          >
                            + Lesson
                          </button>
                          <button
                            onClick={() => openEditModuleModal(module)}
                            className="text-blue-400 hover:text-blue-300"
                          >
                            ✏️
                          </button>
                          <button
                            onClick={() => handleDeleteModule(module.id)}
                            className="text-red-400 hover:text-red-300"
                          >
                            🗑️
                          </button>
                        </div>
                      </div>

                      {/* Lessons */}
                      <div className="space-y-2 ml-4">
                        {(lessons[module.id] || []).map((lesson, lessonIndex) => (
                          <div
                            key={lesson.id}
                            className="flex items-center justify-between bg-slate-900 rounded p-3"
                          >
                            <div className="flex items-center gap-3">
                              <span className="text-xl">{getContentTypeIcon(lesson.content_type)}</span>
                              <div>
                                <p className="text-sm font-medium">
                                  {lessonIndex + 1}. {lesson.title}
                                </p>
                                <div className="flex items-center gap-2 mt-1">
                                  <span className="text-xs text-slate-400">{lesson.content_type}</span>
                                  {lesson.duration_minutes && (
                                    <span className="text-xs text-slate-400">
                                      • {lesson.duration_minutes} min
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                            <button
                              onClick={() => handleDeleteLesson(lesson.id, module.id)}
                              className="text-red-400 hover:text-red-300 text-sm"
                            >
                              🗑️
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          ) : (
            <div className="card text-center py-12 text-slate-400">
              Select a course to view and manage its modules and lessons
            </div>
          )}
        </div>
      </div>

      {/* Course Modal */}
      {showCourseModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-900 rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-bold mb-4">
              {editingCourse ? 'Edit Course' : 'Create New Course'}
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Title *</label>
                <input
                  type="text"
                  value={courseForm.title}
                  onChange={(e) => setCourseForm({ ...courseForm, title: e.target.value })}
                  className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg focus:outline-none focus:border-primary-500"
                  placeholder="e.g., Basic Tax Preparation"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Description *</label>
                <textarea
                  value={courseForm.description}
                  onChange={(e) => setCourseForm({ ...courseForm, description: e.target.value })}
                  className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg focus:outline-none focus:border-primary-500"
                  rows={4}
                  placeholder="Describe what students will learn..."
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Level</label>
                  <select
                    value={courseForm.level}
                    onChange={(e) => setCourseForm({ ...courseForm, level: e.target.value as any })}
                    className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg focus:outline-none focus:border-primary-500"
                  >
                    <option value="beginner">Beginner</option>
                    <option value="intermediate">Intermediate</option>
                    <option value="advanced">Advanced</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Status</label>
                  <select
                    value={courseForm.status}
                    onChange={(e) => setCourseForm({ ...courseForm, status: e.target.value as any })}
                    className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg focus:outline-none focus:border-primary-500"
                  >
                    <option value="draft">Draft</option>
                    <option value="published">Published</option>
                    <option value="archived">Archived</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={editingCourse ? handleUpdateCourse : handleCreateCourse}
                className="btn btn-primary flex-1"
              >
                {editingCourse ? 'Update Course' : 'Create Course'}
              </button>
              <button
                onClick={() => {
                  setShowCourseModal(false);
                  setEditingCourse(null);
                  resetCourseForm();
                }}
                className="btn btn-secondary flex-1"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Module Modal */}
      {showModuleModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-900 rounded-lg p-6 max-w-2xl w-full">
            <h3 className="text-xl font-bold mb-4">
              {editingModule ? 'Edit Module' : 'Create New Module'}
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Title *</label>
                <input
                  type="text"
                  value={moduleForm.title}
                  onChange={(e) => setModuleForm({ ...moduleForm, title: e.target.value })}
                  className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg focus:outline-none focus:border-primary-500"
                  placeholder="e.g., Introduction to Tax Forms"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Description</label>
                <textarea
                  value={moduleForm.description}
                  onChange={(e) => setModuleForm({ ...moduleForm, description: e.target.value })}
                  className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg focus:outline-none focus:border-primary-500"
                  rows={3}
                  placeholder="Optional module description..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Order</label>
                <input
                  type="number"
                  value={moduleForm.order_index}
                  onChange={(e) => setModuleForm({ ...moduleForm, order_index: parseInt(e.target.value) })}
                  className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg focus:outline-none focus:border-primary-500"
                  min="0"
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={editingModule ? handleUpdateModule : handleCreateModule}
                className="btn btn-primary flex-1"
              >
                {editingModule ? 'Update Module' : 'Create Module'}
              </button>
              <button
                onClick={() => {
                  setShowModuleModal(false);
                  setEditingModule(null);
                  resetModuleForm();
                }}
                className="btn btn-secondary flex-1"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Lesson Modal */}
      {showLessonModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-900 rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-bold mb-4">Create New Lesson</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Title *</label>
                <input
                  type="text"
                  value={lessonForm.title}
                  onChange={(e) => setLessonForm({ ...lessonForm, title: e.target.value })}
                  className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg focus:outline-none focus:border-primary-500"
                  placeholder="e.g., Understanding Form 1040"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Content Type</label>
                <select
                  value={lessonForm.content_type}
                  onChange={(e) => setLessonForm({ ...lessonForm, content_type: e.target.value as any })}
                  className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg focus:outline-none focus:border-primary-500"
                >
                  <option value="video">Video</option>
                  <option value="document">Document</option>
                  <option value="quiz">Quiz</option>
                  <option value="text">Text</option>
                </select>
              </div>

              {lessonForm.content_type === 'video' && (
                <>
                  <div>
                    <label className="block text-sm font-medium mb-2">Video URL</label>
                    <input
                      type="text"
                      value={lessonForm.video_url}
                      onChange={(e) => setLessonForm({ ...lessonForm, video_url: e.target.value })}
                      className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg focus:outline-none focus:border-primary-500"
                      placeholder="https://... (YouTube, Vimeo, or S3 URL)"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Duration (minutes)</label>
                    <input
                      type="number"
                      value={lessonForm.duration_minutes}
                      onChange={(e) => setLessonForm({ ...lessonForm, duration_minutes: parseInt(e.target.value) })}
                      className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg focus:outline-none focus:border-primary-500"
                      min="0"
                    />
                  </div>
                </>
              )}

              {(lessonForm.content_type === 'text' || lessonForm.content_type === 'document') && (
                <div>
                  <label className="block text-sm font-medium mb-2">Content</label>
                  <textarea
                    value={lessonForm.content}
                    onChange={(e) => setLessonForm({ ...lessonForm, content: e.target.value })}
                    className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg focus:outline-none focus:border-primary-500"
                    rows={6}
                    placeholder={lessonForm.content_type === 'document' ? 'Document URL or content...' : 'Lesson text content...'}
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium mb-2">Order</label>
                <input
                  type="number"
                  value={lessonForm.order_index}
                  onChange={(e) => setLessonForm({ ...lessonForm, order_index: parseInt(e.target.value) })}
                  className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg focus:outline-none focus:border-primary-500"
                  min="0"
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={handleCreateLesson}
                className="btn btn-primary flex-1"
              >
                Create Lesson
              </button>
              <button
                onClick={() => {
                  setShowLessonModal(false);
                  setSelectedModuleForLesson(null);
                  resetLessonForm();
                }}
                className="btn btn-secondary flex-1"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CourseManagement;
